if (!customElements.get('islander-mobile-purchase')) {
  class IslanderMobilePurchase extends HTMLElement {
    connectedCallback() {
      this.productInfo = this.closest('product-info');
      this.stickyButton = this.querySelector('[data-islander-sticky-submit]');
      this.stickyPrice = this.querySelector('[data-islander-sticky-price]');
      this.stickyQuantity = this.querySelector('[data-islander-sticky-quantity-input]');
      this.primaryBuy = this.productInfo?.querySelector('[data-islander-primary-buy]');
      this.shouldShow = false;
      this.hasFormFocus = false;

      if (!this.productInfo || !this.stickyButton || !this.primaryBuy) return;

      this.handleClick = this.handleClick.bind(this);
      this.handleStickySubmit = this.handleStickySubmit.bind(this);
      this.handleQuantityButton = this.handleQuantityButton.bind(this);
      this.handleStickyQuantityChange = this.handleStickyQuantityChange.bind(this);
      this.handleFormFocusIn = this.handleFormFocusIn.bind(this);
      this.handleFormFocusOut = this.handleFormFocusOut.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleViewportChange = this.handleViewportChange.bind(this);
      this.syncFromNativeForm = this.syncFromNativeForm.bind(this);
      this.syncQuantityFromNative = this.syncQuantityFromNative.bind(this);

      this.addEventListener('click', this.handleClick);
      this.addEventListener('change', this.handleStickyQuantityChange);
      this.productInfo.addEventListener('focusin', this.handleFormFocusIn);
      this.productInfo.addEventListener('focusout', this.handleFormFocusOut);
      window.addEventListener('scroll', this.handleViewportChange, { passive: true });
      window.addEventListener('resize', this.handleViewportChange, { passive: true });

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange);
        this.unsubscribeQuantity = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.syncQuantityFromNative);
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
          window.setTimeout(this.syncFromNativeForm, 0);
        });
      }

      this.observer = new IntersectionObserver(this.handleViewportChange, { threshold: [0, 0.05, 1] });
      this.observer.observe(this.primaryBuy);

      this.rebindSourceControls();
      this.syncFromNativeForm();
      this.syncQuantityFromNative();
      this.handleViewportChange();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('change', this.handleStickyQuantityChange);
      this.productInfo?.removeEventListener('focusin', this.handleFormFocusIn);
      this.productInfo?.removeEventListener('focusout', this.handleFormFocusOut);
      window.removeEventListener('scroll', this.handleViewportChange);
      window.removeEventListener('resize', this.handleViewportChange);
      this.sourceQuantity?.removeEventListener('input', this.syncQuantityFromNative);
      this.sourceQuantity?.removeEventListener('change', this.syncQuantityFromNative);
      this.unsubscribeVariant?.();
      this.unsubscribeQuantity?.();
      this.unsubscribeCart?.();
      this.nativeButtonObserver?.disconnect();
      this.observer?.disconnect();
      if (this.viewportFrame) cancelAnimationFrame(this.viewportFrame);
    }

    handleClick(event) {
      const quantityButton = event.target.closest('[data-islander-sticky-quantity-change]');
      if (quantityButton && this.contains(quantityButton)) {
        this.handleQuantityButton(quantityButton);
        return;
      }

      if (event.target.closest('[data-islander-sticky-submit]')) this.handleStickySubmit();
    }

    handleStickySubmit() {
      const nativeButton = this.getNativeSubmit();
      if (!nativeButton || nativeButton.disabled || nativeButton.getAttribute('aria-disabled') === 'true') return;
      nativeButton.click();
    }

    handleQuantityButton(button) {
      const direction = Number(button.dataset.islanderStickyQuantityChange) || 0;
      const sourceQuantity = this.getSourceQuantity();
      if (!sourceQuantity || !direction) return;

      const step = Math.max(1, Number(sourceQuantity.step) || 1);
      const minimum = Number(sourceQuantity.min) || step;
      const maximum = sourceQuantity.max ? Number(sourceQuantity.max) : Number.POSITIVE_INFINITY;
      const current = Number(sourceQuantity.value) || minimum;
      sourceQuantity.value = String(Math.min(maximum, Math.max(minimum, current + direction * step)));
      sourceQuantity.dispatchEvent(new Event('input', { bubbles: true }));
      sourceQuantity.dispatchEvent(new Event('change', { bubbles: true }));
      this.syncQuantityFromNative();
    }

    handleStickyQuantityChange(event) {
      if (event.target !== this.stickyQuantity) return;
      const sourceQuantity = this.getSourceQuantity();
      if (!sourceQuantity || !this.stickyQuantity) return;

      const step = Math.max(1, Number(sourceQuantity.step) || 1);
      const minimum = Number(sourceQuantity.min) || step;
      const maximum = sourceQuantity.max ? Number(sourceQuantity.max) : Number.POSITIVE_INFINITY;
      const requested = Number(this.stickyQuantity.value) || minimum;
      sourceQuantity.value = String(Math.min(maximum, Math.max(minimum, requested)));
      sourceQuantity.dispatchEvent(new Event('input', { bubbles: true }));
      sourceQuantity.dispatchEvent(new Event('change', { bubbles: true }));
      this.syncQuantityFromNative();
    }

    handleFormFocusIn(event) {
      if (!event.target.matches('input, select, textarea, [role="combobox"]')) return;
      if (this.contains(event.target)) return;
      this.hasFormFocus = true;
      this.updateVisibility();
    }

    handleFormFocusOut() {
      requestAnimationFrame(() => {
        this.hasFormFocus =
          this.productInfo?.contains(document.activeElement) &&
          !this.contains(document.activeElement) &&
          document.activeElement.matches('input, select, textarea, [role="combobox"]');
        this.updateVisibility();
      });
    }

    handleVariantChange(event) {
      if (String(event?.data?.sectionId) !== String(this.dataset.section)) return;
      this.syncSummaryFromDocument(event.data.html);
      requestAnimationFrame(() => {
        this.rebindSourceControls();
        this.syncFromNativeForm();
        this.syncQuantityFromNative();
      });
    }

    handleViewportChange() {
      if (this.viewportFrame) return;
      this.viewportFrame = requestAnimationFrame(() => {
        this.viewportFrame = null;
        this.shouldShow = this.primaryBuy?.getBoundingClientRect().bottom < 0;
        this.updateVisibility();
      });
    }

    getNativeSubmit() {
      return this.productInfo?.querySelector('[data-islander-native-submit]');
    }

    getSourceQuantity() {
      return this.productInfo?.querySelector('.product-form__quantity input[name="quantity"]');
    }

    rebindSourceControls() {
      const nextQuantity = this.getSourceQuantity();
      if (nextQuantity !== this.sourceQuantity) {
        this.sourceQuantity?.removeEventListener('input', this.syncQuantityFromNative);
        this.sourceQuantity?.removeEventListener('change', this.syncQuantityFromNative);
        this.sourceQuantity = nextQuantity;
        this.sourceQuantity?.addEventListener('input', this.syncQuantityFromNative);
        this.sourceQuantity?.addEventListener('change', this.syncQuantityFromNative);
      }

      const nextButton = this.getNativeSubmit();
      if (nextButton !== this.nativeButton) {
        this.nativeButtonObserver?.disconnect();
        this.nativeButton = nextButton;
        if (this.nativeButton) {
          this.nativeButtonObserver = new MutationObserver(this.syncFromNativeForm);
          this.nativeButtonObserver.observe(this.nativeButton, {
            attributes: true,
            attributeFilter: ['aria-disabled', 'class', 'data-islander-cart-quantity', 'disabled'],
            childList: true,
            characterData: true,
            subtree: true,
          });
        }
      }
    }

    syncFromNativeForm() {
      this.rebindSourceControls();
      const nativeButton = this.nativeButton;
      if (!nativeButton || !this.stickyButton) return;

      const isAdded = nativeButton.classList.contains('is-added');
      const unavailable = nativeButton.disabled || nativeButton.getAttribute('aria-disabled') === 'true';
      const nativeCopy = nativeButton.querySelector('[data-islander-submit-label-copy]')?.textContent?.trim();
      const nativeCartLabel = nativeButton.querySelector('[data-islander-cart-quantity-label]');
      const stickyCopy = this.stickyButton.querySelector('[data-islander-sticky-submit-copy]');
      const stickyCart = this.stickyButton.querySelector('[data-islander-sticky-cart-label]');

      this.stickyButton.disabled = unavailable;
      this.stickyButton.classList.toggle('is-added', isAdded);
      if (stickyCopy) {
        stickyCopy.textContent = isAdded ? 'Added' : nativeCopy || window.variantStrings?.addToCart || 'Add to cart';
      }
      if (stickyCart) {
        const hasCartQuantity = nativeCartLabel && !nativeCartLabel.hidden && !isAdded;
        stickyCart.hidden = !hasCartQuantity;
        stickyCart.textContent = hasCartQuantity ? nativeCartLabel.textContent.trim() : '';
      }

      const currentPrice = this.productInfo?.querySelector(`#price-${CSS.escape(this.dataset.section)}`);
      if (currentPrice && this.stickyPrice) this.stickyPrice.innerHTML = currentPrice.innerHTML;
    }

    syncQuantityFromNative() {
      this.rebindSourceControls();
      const sourceQuantity = this.sourceQuantity;
      if (!sourceQuantity || !this.stickyQuantity) return;

      ['min', 'max', 'step'].forEach((attribute) => {
        if (sourceQuantity.hasAttribute(attribute)) {
          this.stickyQuantity.setAttribute(attribute, sourceQuantity.getAttribute(attribute));
        } else {
          this.stickyQuantity.removeAttribute(attribute);
        }
      });
      this.stickyQuantity.value = sourceQuantity.value;

      const current = Number(sourceQuantity.value);
      const minimum = Number(sourceQuantity.min);
      const maximum = sourceQuantity.max ? Number(sourceQuantity.max) : Number.POSITIVE_INFINITY;
      const minus = this.querySelector('[data-islander-sticky-quantity-change="-1"]');
      const plus = this.querySelector('[data-islander-sticky-quantity-change="1"]');
      if (minus) minus.disabled = current <= minimum;
      if (plus) plus.disabled = current >= maximum;
    }

    syncSummaryFromDocument(html) {
      const incoming = html?.getElementById?.(this.id);
      if (!incoming) return;

      const incomingImage = incoming.querySelector('[data-islander-sticky-image]');
      const currentImage = this.querySelector('[data-islander-sticky-image]');
      if (incomingImage && currentImage) {
        ['src', 'srcset', 'sizes', 'width', 'height', 'alt'].forEach((attribute) => {
          const value = incomingImage.getAttribute(attribute);
          if (value === null) currentImage.removeAttribute(attribute);
          else currentImage.setAttribute(attribute, value);
        });
      }

      const incomingVariant = incoming.querySelector('[data-islander-sticky-variant]');
      const currentVariant = this.querySelector('[data-islander-sticky-variant]');
      if (incomingVariant && currentVariant) {
        currentVariant.textContent = incomingVariant.textContent.trim();
        currentVariant.hidden = incomingVariant.hidden;
      }

      const incomingPrice = incoming.querySelector('[data-islander-sticky-price]');
      if (incomingPrice && this.stickyPrice) this.stickyPrice.innerHTML = incomingPrice.innerHTML;
    }

    updateVisibility() {
      const shouldHide = !this.shouldShow || this.hasFormFocus;
      this.hidden = shouldHide;
      this.setAttribute('aria-hidden', String(shouldHide));
    }
  }

  customElements.define('islander-mobile-purchase', IslanderMobilePurchase);
}

(() => {
  const markerClass = 'islander-variant-picker__sale-marker';
  const productSelector = 'product-info';
  const visibleOptionSelector = '.globo-swatch-product-detail .select-option[data-value]';
  const pendingProducts = new Set();
  let frameId;

  const saleOptions = (productInfo) => {
    const options = new Map();

    productInfo.querySelectorAll('variant-selects [data-islander-sale-swatch="true"]').forEach((label) => {
      const value = label.control?.value || label.previousElementSibling?.value;
      const saleCopy = label.querySelector(`.${markerClass}`)?.textContent?.trim();
      if (value) options.set(value, saleCopy || 'Sale');
    });

    return options;
  };

  const syncProduct = (productInfo) => {
    const options = saleOptions(productInfo);

    productInfo.querySelectorAll(visibleOptionSelector).forEach((option) => {
      const saleCopy = options.get(option.dataset.value);
      const input = option.querySelector('input[type="radio"]');
      const label = option.querySelector('label');
      if (!label) return;

      label.toggleAttribute('data-islander-sale-swatch', Boolean(saleCopy));
      let marker = label.querySelector(`.${markerClass}`);

      if (saleCopy && !marker) {
        marker = document.createElement('span');
        marker.className = markerClass;
        label.appendChild(marker);
      }

      if (marker) {
        if (saleCopy) marker.textContent = saleCopy;
        else marker.remove();
      }

      if (!input) return;
      const baseLabel = input.dataset.islanderBaseAriaLabel || input.getAttribute('aria-label') || option.dataset.value;
      input.dataset.islanderBaseAriaLabel = baseLabel;
      input.setAttribute('aria-label', saleCopy ? `${baseLabel}, ${saleCopy}` : baseLabel);
    });
  };

  const flush = () => {
    frameId = null;
    pendingProducts.forEach(syncProduct);
    pendingProducts.clear();
  };

  const queueProduct = (productInfo) => {
    if (!productInfo) return;
    pendingProducts.add(productInfo);
    if (!frameId) frameId = requestAnimationFrame(flush);
  };

  const queueFromNode = (node) => {
    if (!(node instanceof Element)) return;
    if (node.matches(productSelector)) queueProduct(node);
    queueProduct(node.closest(productSelector));
    node.querySelectorAll(productSelector).forEach(queueProduct);
  };

  document.querySelectorAll(productSelector).forEach(queueProduct);
  new MutationObserver((records) => {
    records.forEach((record) => record.addedNodes.forEach(queueFromNode));
  }).observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('shopify:section:load', (event) => queueFromNode(event.target));
})();
